import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Icon } from '@/components/Icon';
import { TASKBAR_H } from '@/wm/constants';
import type { MenuItem } from './types';
import css from './MenuBar.module.css';

/** How long the pointer must rest on a parent row before its submenu opens. */
const SUBMENU_DELAY = 250;
/** Submenus tuck under the parent's border, as they do in Windows. */
const SUBMENU_OVERLAP = 3;
/** Border + padding above the first row, so a submenu lines up with its parent. */
const SUBMENU_RISE = 3;
/** Keep popups this far from the viewport edge when flipping or clamping. */
const EDGE = 2;

/**
 * Selector for the `data-xp-menupopup` attribute every popup carries. The root
 * popup dismisses itself on an outside pointerdown, and submenus live in their
 * own portals — so "inside" has to mean "inside *any* popup", not "inside my
 * own subtree".
 */
const POPUP_SELECTOR = '[data-xp-menupopup]';

export type MenuCloseReason = 'activate' | 'escape' | 'outside' | 'blur';

export interface SubmenuAnchor {
  /** Viewport left edge of the parent popup — the flip target on overflow. */
  left: number;
  /** Viewport right edge of the parent popup. */
  right: number;
}

export interface DropdownMenuProps {
  items: MenuItem[];
  /** Preferred viewport left edge. */
  x: number;
  /** Preferred viewport top edge. */
  y: number;
  /**
   * Dismisses this popup. `reason` is `'activate'` when an item was chosen
   * anywhere below this level, which is how the whole chain tears itself down.
   */
  onClose: (reason?: MenuCloseReason) => void;
  /** Set when this popup is a submenu; changes flipping and dismissal duties. */
  submenuOf?: SubmenuAnchor;
  /** Highlight the first row on mount — used when opened from the keyboard. */
  autoHighlight?: boolean;
}

interface OpenSubmenu {
  index: number;
  x: number;
  y: number;
  anchor: SubmenuAnchor;
  viaKeyboard: boolean;
}

/**
 * `"&File"` → `F` underlined, the ampersand removed; `"&&"` is a literal one.
 * Shared with `MenuBar`, which renders the same markup for its titles.
 */
export function renderMnemonic(label: string): ReactNode {
  if (!label.includes('&')) return label;

  const out: ReactNode[] = [];
  let text = '';
  let used = false;

  for (let i = 0; i < label.length; i += 1) {
    const ch = label[i];
    if (ch !== '&') {
      text += ch;
      continue;
    }
    const next = label[i + 1];
    if (next === '&') {
      text += '&';
      i += 1;
      continue;
    }
    if (next === undefined || used) continue; // trailing or surplus marker
    if (text) {
      out.push(text);
      text = '';
    }
    out.push(<u key={`m${i}`}>{next}</u>);
    i += 1;
    used = true;
  }

  if (text) out.push(text);
  return out;
}

/** Next selectable row in `dir`, wrapping, skipping separators. */
function step(items: MenuItem[], from: number, dir: 1 | -1): number {
  const n = items.length;
  if (n === 0) return -1;
  let i = from < 0 ? (dir === 1 ? -1 : n) : from;
  for (let k = 0; k < n; k += 1) {
    i = (i + dir + n) % n;
    if (!items[i].separator) return i;
  }
  return -1;
}

function firstSelectable(items: MenuItem[]): number {
  return step(items, -1, 1);
}

/**
 * One level of a Windows menu, portalled to `document.body` so no window's
 * overflow can clip it. Submenus are just more `DropdownMenu`s rendered inside
 * this one's React tree — which also means their key events bubble up through
 * here to the `MenuBar`, exactly as menu keyboard navigation expects.
 */
export function DropdownMenu({
  items,
  x,
  y,
  onClose,
  submenuOf,
  autoHighlight = false,
}: DropdownMenuProps) {
  const rootRef = useRef<HTMLUListElement>(null);
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const timerRef = useRef<number | null>(null);
  const baseId = useId();

  const [pos, setPos] = useState({ left: x, top: y });
  const [highlight, setHighlight] = useState(() => (autoHighlight ? firstSelectable(items) : -1));
  const [sub, setSub] = useState<OpenSubmenu | null>(null);

  /* ---------------------------------------------------------------- layout */

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    // Rows hidden behind the taskbar cannot be clicked — the taskbar swallows
    // the pointer — so the popup's world ends at the bottom of the desktop.
    const vh = window.innerHeight - TASKBAR_H;

    let left = x;
    if (left + width > vw - EDGE) {
      left = submenuOf ? submenuOf.left - width + SUBMENU_OVERLAP : vw - width - EDGE;
    }
    left = Math.max(EDGE, left);

    let top = y;
    if (top + height > vh - EDGE) {
      const flipped = submenuOf ? vh - height - EDGE : y - height;
      top = flipped >= EDGE ? flipped : vh - height - EDGE;
    }
    // The anchor itself can sit below the desktop, so a flip is not enough on
    // its own; pull the box back inside unconditionally.
    top = Math.max(EDGE, Math.min(top, vh - height - EDGE));

    setPos((prev) => (prev.left === left && prev.top === top ? prev : { left, top }));
  }, [x, y, items, submenuOf]);

  /* ----------------------------------------------------------------- focus */

  // The popup owns the keyboard while it is up; `MenuBar` puts focus back.
  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  // When a submenu goes away — dismissed, or hovered away from — take the
  // keyboard back, unless something else (a dialog, say) has claimed it.
  useEffect(() => {
    if (sub) return;
    const el = rootRef.current;
    if (!el) return;
    if (document.activeElement === null || document.activeElement === document.body) {
      el.focus({ preventScroll: true });
    }
  }, [sub]);

  /* ------------------------------------------------------------- dismissal */

  // Callers rebuild their menus on every render, so `onClose` is a fresh
  // closure each time; the listeners below must not re-subscribe for that.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (submenuOf) return; // only the root of a chain listens
    // The gesture that opened us is still propagating towards `document`;
    // ignoring anything stamped before this moment stops it closing us again.
    const openedAt = performance.now();

    const onPointerDown = (e: PointerEvent) => {
      if (e.timeStamp <= openedAt) return;
      const target = e.target;
      if (target instanceof Element && target.closest(POPUP_SELECTOR)) return;
      onCloseRef.current('outside');
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Only reached when the popup lost focus; otherwise it handles Escape.
      if (e.key === 'Escape') onCloseRef.current('escape');
    };
    const onBlur = () => onCloseRef.current('blur');
    const onResize = () => onCloseRef.current('outside');

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', onBlur);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('resize', onResize);
    };
  }, [submenuOf]);

  /* ------------------------------------------------------------- submenus */

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => clearTimer, []);

  const openSubmenu = (index: number, viaKeyboard: boolean) => {
    clearTimer();
    const row = rowRefs.current[index];
    const root = rootRef.current;
    if (!row || !root) return;
    const r = row.getBoundingClientRect();
    const p = root.getBoundingClientRect();
    setHighlight(index);
    setSub({
      index,
      x: p.right - SUBMENU_OVERLAP,
      y: r.top - SUBMENU_RISE,
      anchor: { left: p.left, right: p.right },
      viaKeyboard,
    });
  };

  /** `index === null` schedules a close instead of an open. */
  const scheduleSubmenu = (index: number | null) => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (index === null) setSub(null);
      else openSubmenu(index, false);
    }, SUBMENU_DELAY);
  };

  const closeSubmenu = (reason?: MenuCloseReason) => {
    clearTimer();
    setSub(null);
    // A choice made deep in the chain closes every level above it too.
    if (reason === 'activate') onClose('activate');
  };

  /* ----------------------------------------------------------- activation */

  const activate = (index: number) => {
    const item = items[index];
    if (!item || item.separator || item.disabled) return;
    if (item.items && item.items.length > 0) {
      openSubmenu(index, false);
      return;
    }
    onClose('activate');
    item.onSelect?.();
  };

  /* -------------------------------------------------------------- pointer */

  const onRowEnter = (index: number) => {
    const item = items[index];
    if (!item || item.separator) return;
    if (item.disabled) {
      setHighlight(-1);
      if (sub) scheduleSubmenu(null);
      else clearTimer();
      return;
    }
    setHighlight(index);
    if (item.items && item.items.length > 0) {
      if (sub?.index === index) clearTimer();
      else scheduleSubmenu(index);
    } else if (sub) {
      scheduleSubmenu(null);
    } else {
      clearTimer();
    }
  };

  const onPopupLeave = () => {
    clearTimer();
    if (!sub) setHighlight(-1);
  };

  /* ------------------------------------------------------------- keyboard */

  const onKeyDown = (e: ReactKeyboardEvent<HTMLUListElement>) => {
    if (sub) return; // the open submenu has focus and gets first refusal
    const consume = () => {
      e.preventDefault();
      e.stopPropagation();
    };

    switch (e.key) {
      case 'ArrowDown':
        consume();
        setHighlight(step(items, highlight, 1));
        break;
      case 'ArrowUp':
        consume();
        setHighlight(step(items, highlight, -1));
        break;
      case 'Home':
        consume();
        setHighlight(step(items, -1, 1));
        break;
      case 'End':
        consume();
        setHighlight(step(items, -1, -1));
        break;
      case 'Enter':
      case ' ':
        if (highlight >= 0) {
          consume();
          activate(highlight);
        }
        break;
      case 'Escape':
        consume();
        onClose('escape');
        break;
      case 'Tab':
        // A root popup swallows Tab; inside a submenu it rises to the MenuBar,
        // which tears the whole chain down at once.
        e.preventDefault();
        if (!submenuOf) {
          e.stopPropagation();
          onClose('escape');
        }
        break;
      case 'ArrowRight': {
        const item = items[highlight];
        if (item && !item.disabled && item.items && item.items.length > 0) {
          consume();
          openSubmenu(highlight, true);
        }
        // Otherwise it bubbles: Right on a leaf moves to the next menu title.
        break;
      }
      case 'ArrowLeft':
        if (submenuOf) {
          consume();
          onClose('escape');
        }
        // At the root it bubbles to the MenuBar, which steps one title left.
        break;
      default:
        break;
    }
  };

  /* -------------------------------------------------------------- rendering */

  const itemId = (index: number) => `${baseId}-item-${index}`;
  const hasArrow = items.some((item) => item.items && item.items.length > 0);

  const popup = (
    <ul
      data-xp-menupopup=""
      ref={rootRef}
      className={css.dropdown}
      style={{ left: pos.left, top: pos.top }}
      role="menu"
      tabIndex={-1}
      aria-activedescendant={highlight >= 0 ? itemId(highlight) : undefined}
      onKeyDown={onKeyDown}
      onPointerLeave={onPopupLeave}
    >
      {items.map((item, index) => {
        if (item.separator) {
          return <li key={`sep-${index}`} className={css.separator} role="separator" />;
        }

        const submenu = item.items && item.items.length > 0 ? item.items : undefined;
        // The *presence* of `checked`/`radio` says the item is checkable; the
        // value says whether it is on. Deriving the role from the value would
        // make an off toggle announce as a plain command.
        const role =
          item.radio !== undefined
            ? 'menuitemradio'
            : item.checked !== undefined
              ? 'menuitemcheckbox'
              : 'menuitem';

        return (
          <li
            key={`${item.label ?? 'item'}-${index}`}
            className={css.row}
            role="none"
            ref={(el) => {
              rowRefs.current[index] = el;
            }}
            onPointerEnter={() => onRowEnter(index)}
          >
            <button
              type="button"
              id={itemId(index)}
              role={role}
              className={clsx(css.item, highlight === index && css.active)}
              tabIndex={-1}
              disabled={item.disabled}
              aria-haspopup={submenu ? true : undefined}
              aria-expanded={submenu ? sub?.index === index : undefined}
              aria-checked={
                role === 'menuitem' ? undefined : Boolean(item.radio ?? item.checked)
              }
              onPointerUp={(e) => {
                // Windows menus commit on release, which also makes
                // press-drag-release across the whole chain work.
                if (e.button !== 0) return;
                activate(index);
              }}
              onClick={(e) => {
                // `detail === 0` means Enter/Space or assistive tech; real
                // mouse clicks were already served on pointerup above.
                if (e.detail !== 0) return;
                activate(index);
              }}
            >
              <span className={css.gutter}>
                {item.icon ? (
                  <Icon src={item.icon} size={16} />
                ) : item.checked ? (
                  <span className={css.glyph}>✓</span>
                ) : item.radio ? (
                  <span className={css.bullet}>•</span>
                ) : null}
              </span>
              <span className={css.label}>{renderMnemonic(item.label ?? '')}</span>
              {item.shortcut ? <span className={css.shortcut}>{item.shortcut}</span> : null}
              {submenu ? (
                <span className={css.arrow} aria-hidden="true">
                  ▸
                </span>
              ) : hasArrow ? (
                <span className={css.arrowSpacer} />
              ) : null}
            </button>
          </li>
        );
      })}

      {sub && items[sub.index]?.items ? (
        <DropdownMenu
          /* One submenu replacing another has to be a new popup, not the old
             one re-rendered with different items. */
          key={sub.index}
          items={items[sub.index].items ?? []}
          x={sub.x}
          y={sub.y}
          submenuOf={sub.anchor}
          autoHighlight={sub.viaKeyboard}
          onClose={closeSubmenu}
        />
      ) : null}
    </ul>
  );

  return createPortal(popup, document.body);
}
